import { createCometTexture, COMET_HEAD_X, COMET_HEAD_Y } from './cometTexture';
import React, { useEffect, useRef } from 'react';
import {
  ANIMATION_SETTINGS_CHANGE_EVENT,
  loadLetterAnimationSettings,
} from '../lib/animationSettings';

interface Star {
  x: number;
  y: number;
  radius: number;
  alpha: number;
  color: string;
  phase: number;
  speed: number;
}

interface NebulaCloud { x: number; y: number; radius: number; color: string; alpha: number; }
interface Asteroid { x: number; y: number; size: number; speed: number; drift: number; alpha: number; }
interface Comet { x: number; y: number; progress: number; speed: number; angle: number; vx: number; vy: number; texture?: HTMLCanvasElement; }
interface PlanetTexture { image: HTMLImageElement; sx: number; sy: number; sw: number; sh: number; }

const STAR_COLORS = ['224,218,255', '190,178,255', '255,255,255', '110,205,255', '255,143,226', '255,218,157', '125,145,255'];
const NEBULA_COLORS = ['var(--fp-starlight-nebula-blue)', 'var(--fp-starlight-nebula-cyan)', 'var(--fp-starlight-nebula-violet)', 'var(--fp-starlight-nebula-pink)'];
const MAX_STARS = 2400;

function seededRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 0x100000000;
  };
}

export const StarlightCanvas: React.FC<{ density: number }> = ({ density }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const densityRef = useRef(density);
  densityRef.current = density;

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d', { alpha: true });
    if (!canvas || !context) return undefined;

    let width = 0;
    let height = 0;
    let frame = 0;
    let stars: Star[] = [];
    let clouds: NebulaCloud[] = [];
    let asteroids: Asteroid[] = [];
    let comet: Comet | null = null;
    let active = !document.hidden;
    let starTwinkleEnabled = loadLetterAnimationSettings().starlightStarTwinkle;
    let starTwinkleSpeed = loadLetterAnimationSettings().starlightTwinkleSpeed;
    const motionAllowed = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const startedAt = performance.now();
    const random = seededRandom(910241);
    const rootStyle = getComputedStyle(document.documentElement);
    const planetTextures: Record<string, PlanetTexture> = {};
    const spriteSlots: Record<string, [number, number]> = { earth: [0, 0], saturn: [1, 0], jupiter: [0, 1], nebula: [1, 1] };
    for (const name of ['earth', 'jupiter', 'saturn']) {
      const image = new Image();
      image.src = '/assets/planets/generated-planet-sprite.png';
      const [sx, sy] = spriteSlots[name];
      planetTextures[name] = { image, sx, sy, sw: 768, sh: 512 };
    }
    const resolveColor = (token: string) => token.startsWith('var(')
      ? rootStyle.getPropertyValue(token.slice(4, -1)).trim()
      : token;
    // CSS 变量可能保存为 `rgb(...)`/`rgba(...)`。云团绘制会再次组装
    // `rgba(...)`，因此这里统一提取 RGB 通道，避免产生 `rgba(rgba(...), …)`。
    const toRgbChannels = (value: string): string => {
      const channels = value.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
      if (channels) return `${channels[1]},${channels[2]},${channels[3]}`;
      // 主题切换的首帧可能读到尚未解析完成的 CSS 变量，不能把任意
      // 中间值拼进 Canvas 颜色函数；直接使用安全的星云蓝作为降级值。
      return /^\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*$/.test(value) ? value : '38,105,255';
    };

    const rebuildStars = () => {
      const amount = Math.min(MAX_STARS, Math.round((width * height / 820) * (densityRef.current / 55)));
      stars = Array.from({ length: amount }, () => {
        const x = random() * width;
        const y = random() < 0.42
          ? height * 0.5 + (x - width * 0.5) * 0.33 + (random() - 0.5) * height * 0.28
          : random() * height;
        return {
          x,
          y: Math.max(0, Math.min(height, y)),
          radius: random() < 0.9 ? 0.35 + random() * 0.9 : 1.3 + random() * 1.1,
          alpha: 0.24 + random() * 0.72,
          color: STAR_COLORS[Math.floor(random() * STAR_COLORS.length)],
          phase: random() * Math.PI * 2,
          speed: 0.35 + random() * 1.1,
        };
      });
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      clouds = Array.from({ length: 34 }, () => ({
        x: random() * width,
        y: random() * height,
        radius: width * (0.08 + random() * 0.24),
        color: toRgbChannels(resolveColor(NEBULA_COLORS[Math.floor(random() * NEBULA_COLORS.length)])),
        alpha: 0.16 + random() * 0.22,
      }));
      asteroids = Array.from({ length: Math.max(8, Math.round(10 + densityRef.current * 0.16)) }, () => ({
        x: random() * width,
        y: random() * height,
        size: 0.45 + random() * 1.15,
        speed: 0.04 + random() * 0.12,
        drift: (random() - 0.5) * 0.06,
        alpha: 0.16 + random() * 0.3,
      }));
      rebuildStars();
      draw(performance.now());
    };

    const draw = (time: number) => {
      context.clearRect(0, 0, width, height);
      // 斜向银河带：把高饱和色云团集中到一条宽带中。
      context.save();
      context.translate(width * 0.5, height * 0.5);
      context.rotate(-0.32);
      const band = context.createLinearGradient(-width, 0, width, 0);
      band.addColorStop(0, 'rgba(42,102,255,0)');
      band.addColorStop(0.25, 'rgba(80,120,255,.16)');
      band.addColorStop(0.5, 'rgba(208,87,255,.28)');
      band.addColorStop(0.68, 'rgba(0,205,255,.2)');
      band.addColorStop(1, 'rgba(42,102,255,0)');
      context.fillStyle = band;
      context.filter = 'blur(20px)';
      context.fillRect(-width, -height * 0.17, width * 2, height * 0.34);
      context.restore();
      context.filter = 'none';
      const orbitTime = motionAllowed ? (time - startedAt) / 1000 : 0;
      context.save();
      context.translate(width / 2, height / 2);
      context.rotate(orbitTime * Math.PI * 2 / 5400);
      context.translate(-width / 2, -height / 2);
      for (const cloud of clouds) {
        const gradient = context.createRadialGradient(cloud.x, cloud.y, 0, cloud.x, cloud.y, cloud.radius);
        gradient.addColorStop(0, `rgba(${cloud.color},${cloud.alpha})`);
        gradient.addColorStop(0.42, `rgba(${cloud.color},${cloud.alpha * 0.35})`);
        gradient.addColorStop(1, `rgba(${cloud.color},0)`);
        context.fillStyle = gradient;
        context.fillRect(cloud.x - cloud.radius, cloud.y - cloud.radius, cloud.radius * 2, cloud.radius * 2);
      }
      // 多层径向渐变模拟蓝紫星云，保持低成本并适配移动端 Canvas 2D。
      const nebulae = [
        [0.18, 0.28, 0.46, 0.42, '68,82,255', 0.34],
        [0.62, 0.42, 0.52, 0.34, '21,169,255', 0.3],
        [0.42, 0.58, 0.38, 0.55, '150,62,235', 0.28],
        [0.82, 0.78, 0.44, 0.4, '21,111,220', 0.28],
        [0.08, 0.9, 0.3, 0.3, '33,204,255', 0.22],
      ] as const;
      for (const [x, y, radius, aspect, color, alpha] of nebulae) {
        const gradient = context.createRadialGradient(width * x, height * y, 0, width * x, height * y, width * radius);
        gradient.addColorStop(0, `rgba(${color},${alpha})`);
        gradient.addColorStop(Math.min(0.55, aspect), `rgba(${color},${alpha * 0.38})`);
        gradient.addColorStop(1, `rgba(${color},0)`);
        context.save();
        context.scale(1, aspect);
        context.fillStyle = gradient;
        context.fillRect(0, 0, width, height / aspect);
        context.restore();
      }
      context.restore();
      // 行星沿中心椭圆轨道极慢公转，一周分别需要 12、18、25 分钟。
      const header = document.querySelector('.fp-mobile-header')?.getBoundingClientRect();
      const navigation = document.querySelector('.fp-mobile-tabbar')?.getBoundingClientRect();
      const contentTop = header?.bottom ?? 58;
      const contentBottom = navigation?.top ?? height - 68;
      const centerY = (contentTop + contentBottom) / 2;
      context.save();
      context.beginPath();
      context.rect(0, contentTop, width, Math.max(0, contentBottom - contentTop));
      context.clip();
      const planets = [
        { name: 'earth', phase: Math.PI * 1.25, amplitude: 0.2, period: 105, size: 23, bright: '#d7ecff', mid: '#487bdc', dark: '#111c43', ring: false },
        { name: 'saturn', phase: Math.PI * 1.75, amplitude: 0.18, period: 135, size: 18, bright: '#f7ddff', mid: '#aa63d2', dark: '#26143f', ring: true },
        { name: 'jupiter', phase: Math.PI * 0.25, amplitude: 0.2, period: 165, size: 14, bright: '#ffe8c9', mid: '#d18d67', dark: '#362131', ring: false },
      ];
      const orbitX = Math.max(74, width * 0.5 - 18);
      const orbitY = Math.max(100, (contentBottom - contentTop) * 0.5 - 18);
      for (const planet of planets) {
        const angle = planet.phase + Math.sin(orbitTime * Math.PI * 2 / planet.period) * planet.amplitude;
        const x = width / 2 + Math.cos(angle) * orbitX;
        const y = centerY + Math.sin(angle) * orbitY;
        // 行星只保留本体和环，避免旁侧出现白色/发灰的外发光晕。
        const surface = context.createRadialGradient(x - planet.size * 0.42, y - planet.size * 0.38, 0, x, y, planet.size);
        surface.addColorStop(0, planet.bright);
        surface.addColorStop(0.45, planet.mid);
        surface.addColorStop(1, planet.dark);
        context.fillStyle = surface;
        context.beginPath(); context.arc(x, y, planet.size, 0, Math.PI * 2); context.fill();
        const texture = planetTextures[planet.name];
        if (texture?.image.complete && texture.image.naturalWidth > 0) {
          context.save();
          context.beginPath(); context.arc(x, y, planet.size, 0, Math.PI * 2); context.clip();
          context.globalAlpha = 0.92;
          context.drawImage(texture.image, texture.sx * texture.sw, texture.sy * texture.sh, texture.sw, texture.sh, x - planet.size, y - planet.size, planet.size * 2, planet.size * 2);
          context.restore();
        }
        context.fillStyle = 'rgba(255,255,255,.18)';
        context.beginPath();
        context.ellipse(x - planet.size * 0.34, y - planet.size * 0.38, planet.size * 0.24, planet.size * 0.14, -0.35, 0, Math.PI * 2);
        context.fill();
        context.strokeStyle = 'rgba(8,12,45,.42)';
        context.lineWidth = Math.max(1, planet.size * 0.12);
        context.beginPath(); context.arc(x, y, planet.size, 0, Math.PI * 2); context.stroke();
        // 行星表面条纹，避免退化成纯色圆点。
        context.save();
        context.beginPath(); context.arc(x, y, planet.size, 0, Math.PI * 2); context.clip();
        context.globalAlpha = 0.22;
        context.strokeStyle = planet.bright;
        context.lineWidth = 1.4;
        for (let stripe = -2; stripe <= 2; stripe += 1) {
          context.beginPath();
          context.ellipse(x, y + stripe * planet.size * 0.24, planet.size * 0.92, planet.size * 0.08, 0, 0, Math.PI * 2);
          context.stroke();
        }
        context.restore();
        if (planet.ring) {
          context.save();
          context.strokeStyle = 'rgba(250,224,180,.75)';
          context.lineWidth = Math.max(1, planet.size * 0.12);
          context.beginPath();
          context.ellipse(x, y, planet.size * 1.75, planet.size * 0.42, -0.16, 0, Math.PI * 2);
          context.stroke();
          context.restore();
        }
      }
      context.restore();
      const seconds = (time - startedAt) / 1000;
      for (const asteroid of asteroids) {
        asteroid.x += asteroid.speed;
        asteroid.y += asteroid.drift;
        if (asteroid.x > width + 4) asteroid.x = -4;
        if (asteroid.y < -4) asteroid.y = height + 4;
        if (asteroid.y > height + 4) asteroid.y = -4;
        context.beginPath();
        context.fillStyle = `rgba(185,194,255,${asteroid.alpha})`;
        context.arc(asteroid.x, asteroid.y, asteroid.size, 0, Math.PI * 2);
        context.fill();
      }
      // 低频出现的长尾彗星，完整穿越后再等待下一次事件。
      if (!comet && Math.floor(seconds / 12) % 2 === 1) {
        const top = 58;
        const bottom = Math.max(top + 80, height - 68);
        const edge = Math.floor(random() * 4);
        const starts = [
          [-width * 0.12, top + random() * (bottom - top), 0.8 + random() * 0.45, (random() - 0.5) * 0.5],
          [width * 1.12, top + random() * (bottom - top), -0.8 - random() * 0.45, (random() - 0.5) * 0.5],
          [width * (0.08 + random() * 0.84), top - height * 0.12, (random() - 0.5) * 0.5, 0.8 + random() * 0.45],
          [width * (0.08 + random() * 0.84), bottom + height * 0.12, (random() - 0.5) * 0.5, -0.8 - random() * 0.45],
        ] as const;
        const [x, y, vx, vy] = starts[edge];
        // 尾巴与运动方向严格一致：尾部朝运动反方向延伸。
        // 彗星是缓慢巡航事件，不使用流星的瞬时划过速度。
        comet = { x, y, progress: 0, speed: 0.000006, angle: Math.atan2(vy, vx), vx, vy, texture: createCometTexture() };
      }
      if (comet) {
        comet.progress += comet.speed * 16;
        comet.x += comet.vx * width * 0.00015;
        comet.y += comet.vy * height * 0.00015;
        const top = 58;
        const bottom = Math.max(top + 80, height - 68);
        context.save();
        context.beginPath();
        context.rect(0, top, width, bottom - top);
        context.clip();
        // 在统一局部坐标系内绘制连续光雾：尖端朝前，长尾向后展开。
        if (comet.texture) {
          context.save();
          context.translate(comet.x, comet.y);
          context.rotate(comet.angle + Math.PI);
          const scale = Math.max(0.3, Math.min(0.65, width / 1100));
          context.globalCompositeOperation = 'lighter';
          context.imageSmoothingQuality = 'high';
          context.scale(scale, scale);
          context.drawImage(comet.texture, -COMET_HEAD_X, -COMET_HEAD_Y);
          context.restore();
        }
        context.restore();
        if (comet.progress > 1.12) comet = null;
      }
      for (const star of stars) {
        const shimmer = starTwinkleEnabled
          /* 同时改变透明度与半径，确保呼吸变化不只停留在截图难以辨认的细微差异。 */
          ? 0.22 + (0.78 * (0.5 + 0.5 * Math.sin(seconds * star.speed * (0.4 + starTwinkleSpeed / 100 * 1.6) + star.phase)))
          : 1;
        const radiusScale = starTwinkleEnabled ? 0.84 + shimmer * 0.2 : 1;
        context.beginPath();
        context.fillStyle = `rgba(${star.color},${star.alpha * shimmer})`;
        context.arc(star.x, star.y, star.radius * radiusScale, 0, Math.PI * 2);
        context.fill();
        if (star.radius > 1.45) {
          context.beginPath();
          context.fillStyle = `rgba(${star.color},${star.alpha * shimmer * 0.12})`;
          context.arc(star.x, star.y, star.radius * radiusScale * 3.2, 0, Math.PI * 2);
          context.fill();
          context.strokeStyle = `rgba(${star.color},${star.alpha * shimmer * 0.3})`;
          context.lineWidth = 0.5;
          context.beginPath();
          context.moveTo(star.x - star.radius * radiusScale * 4, star.y);
          context.lineTo(star.x + star.radius * radiusScale * 4, star.y);
          context.moveTo(star.x, star.y - star.radius * radiusScale * 4);
          context.lineTo(star.x, star.y + star.radius * radiusScale * 4);
          context.stroke();
        }
      }
      if (active && motionAllowed) {
        frame = window.requestAnimationFrame(draw);
      }
    };

    const handleVisibility = () => {
      active = !document.hidden;
      window.cancelAnimationFrame(frame);
      if (active) frame = window.requestAnimationFrame(draw);
    };
    const handleAnimationSettingsChange = () => {
      const settings = loadLetterAnimationSettings();
      starTwinkleEnabled = settings.starlightStarTwinkle;
      starTwinkleSpeed = settings.starlightTwinkleSpeed;
      /* 密度变化只重建星点数组，保留星云、行星和其他背景状态。 */
      rebuildStars();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener(ANIMATION_SETTINGS_CHANGE_EVENT, handleAnimationSettingsChange);
    resize();

    return () => {
      resizeObserver.disconnect();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener(ANIMATION_SETTINGS_CHANGE_EVENT, handleAnimationSettingsChange);
      window.cancelAnimationFrame(frame);
    };
  }, [density]);

  return <canvas ref={canvasRef} className="fp-starlight-canvas" aria-hidden="true" />;
};

